import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileCheck2, IdCard } from 'lucide-react';
import SignaturePad from '../components/engagement/SignaturePad';
import { getPublicSettings } from '../utils/appSettings';
import { contractorAgreementFullName } from '../utils/contractorAgreement';
import {
  contractorAgreementBodySections,
  contractorAgreementFromPublicPayload,
  contractorAgreementPdfMeta,
} from '../utils/contractorAgreementPdfLayout';
import {
  downloadContractorAgreementPdf,
} from '../utils/contractorAgreementPdf';
import {
  CREW_BOARD_MEDIA,
  resolveCrewBoardHeroUrl,
} from '../utils/crewJobsBoardMedia';
import {
  createContractorOnboardUploadSession,
  fetchPublicCrewOnboard,
  signPublicContractorAgreement,
  submitContractorOnboardForms,
  type PublicCrewOnboardPayload,
} from '../utils/contractorOnboardPublicApi';
import './PublicCrewJobsPage.css';
import './PublicCrewOnboardPage.css';

type PublicAgreement = NonNullable<PublicCrewOnboardPayload['agreement']>;

const ContractorOnboardAgreement: React.FC<{
  payload: PublicAgreement;
  contractorName: string;
  signed: boolean;
  busy: boolean;
  onSign: (sig: { dataUrl: string; signedBy: string }) => void;
}> = ({ payload, contractorName, signed, busy, onSign }) => {
  const agreement = contractorAgreementFromPublicPayload(payload);
  const meta = contractorAgreementPdfMeta(agreement);
  const sections = contractorAgreementBodySections(agreement);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await downloadContractorAgreementPdf(agreement, { persist: false });
    } catch (e) {
      setPdfError((e as Error).message || 'Could not download the signed agreement');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <>
      {sections.map(section => (
        <article key={section.id} className="crew-onboard-section">
          <h3>{section.heading}</h3>
          <p>{section.body}</p>
        </article>
      ))}
      <div className="crew-onboard-sign-blocks">
        <article className="crew-onboard-sign-card">
          <p className="crew-onboard-sign-kicker">{meta.companySign.heading}</p>
          {meta.companySign.dataUrl ? (
            <img
              src={meta.companySign.dataUrl}
              alt={`${meta.companySign.name} signature`}
              className="crew-onboard-sig-img"
            />
          ) : (
            <div className="crew-onboard-sig-line" />
          )}
          <p><strong>{meta.companySign.name}</strong></p>
          <p>{meta.companySign.title}</p>
          <p>{meta.companySign.stamped}</p>
        </article>
        {signed ? (
          <article className="crew-onboard-sign-card">
            <p className="crew-onboard-sign-kicker">{meta.contractorSign.heading}</p>
            {meta.contractorSign.dataUrl ? (
              <img
                src={meta.contractorSign.dataUrl}
                alt={`${meta.contractorSign.name} signature`}
                className="crew-onboard-sig-img"
              />
            ) : (
              <div className="crew-onboard-sig-line" />
            )}
            <p><strong>{meta.contractorSign.name}</strong></p>
            <p>{meta.contractorSign.title}</p>
            <p>{meta.contractorSign.stamped}</p>
          </article>
        ) : (
          <article className="crew-onboard-sign-card crew-onboard-sign-card--pad">
            <p className="crew-onboard-sign-kicker">Contractor</p>
            <p className="crew-board-muted">
              Type your full legal name and sign below to accept this agreement.
            </p>
            <SignaturePad
              initialName={contractorName}
              disabled={busy}
              onSign={onSign}
            />
          </article>
        )}
      </div>
      {signed && (
        <>
          <div className="crew-onboard-audit">
            <p className="crew-onboard-sign-kicker">Signature audit trail</p>
            {meta.audit
              .filter(row => row.label !== 'PDF generated')
              .map(row => (
                <p key={row.label}>
                  <span>{row.label}</span>
                  {row.value}
                </p>
              ))}
            <p className="crew-onboard-binding">{meta.bindingNote}</p>
          </div>
          <p className="crew-board-muted">
            Signed. You are in the 1099 contractor pool. The office can confirm you onto a raise roster.
          </p>
          <button
            type="button"
            className="crew-onboard-pdf-btn"
            disabled={pdfBusy}
            onClick={() => void downloadPdf()}
          >
            {pdfBusy ? 'Preparing PDF…' : 'Download signed PDF'}
          </button>
          {pdfError && <p className="crew-board-error">{pdfError}</p>}
        </>
      )}
    </>
  );
};

const PublicCrewOnboardPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboard, setOnboard] = useState<PublicCrewOnboardPayload | null>(null);
  const [heroSrc, setHeroSrc] = useState<string>(CREW_BOARD_MEDIA.hero);
  const [logoSrc, setLogoSrc] = useState<string>(CREW_BOARD_MEDIA.logo);
  const [w9File, setW9File] = useState<File | null>(null);
  const [idFile, setIdFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    const res = await fetchPublicCrewOnboard(slug);
    setOnboard(res.onboard);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await getPublicSettings();
        if (cancelled) return;
        setHeroSrc(resolveCrewBoardHeroUrl(settings.crewBoardRaiseHero?.url));
        if (settings.engagementLogo?.url) setLogoSrc(settings.engagementLogo.url);
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await load();
      } catch (e) {
        if (!cancelled) setError((e as Error).message || 'This onboarding link is unavailable.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const uploadPacket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !w9File || !idFile) return;
    setBusy(true);
    setActionError(null);
    try {
      const [w9Session, idSession] = await Promise.all([
        createContractorOnboardUploadSession({
          slug,
          kind: 'w9',
          fileName: w9File.name,
          contentType: w9File.type || 'application/pdf',
        }),
        createContractorOnboardUploadSession({
          slug,
          kind: 'id',
          fileName: idFile.name,
          contentType: idFile.type || 'application/octet-stream',
        }),
      ]);
      await Promise.all([
        fetch(w9Session.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': w9File.type || 'application/pdf' },
          body: w9File,
        }),
        fetch(idSession.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': idFile.type || 'application/octet-stream' },
          body: idFile,
        }),
      ]);
      await submitContractorOnboardForms({
        slug,
        w9SessionId: w9Session.sessionId,
        idSessionId: idSession.sessionId,
      });
      await load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleSign = async (sig: { dataUrl: string; signedBy: string }) => {
    if (!slug) return;
    setBusy(true);
    setActionError(null);
    try {
      await signPublicContractorAgreement({ slug, signedBy: sig.signedBy, dataUrl: sig.dataUrl });
      await load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="crew-board crew-onboard">
        <div className="crew-onboard-loading" role="status" aria-live="polite">
          <img
            src={logoSrc}
            alt="Leiper's Creek Timber Frames"
            className="crew-onboard-loading-mark"
            onError={() => {
              if (logoSrc !== CREW_BOARD_MEDIA.logoFallback) setLogoSrc(CREW_BOARD_MEDIA.logoFallback);
            }}
          />
          <div className="crew-onboard-loading-pulse" aria-hidden />
          <p className="crew-onboard-loading-copy">Preparing your onboarding packet…</p>
        </div>
      </div>
    );
  }

  if (error || !onboard) {
    return (
      <div className="crew-board crew-onboard">
        <div className="crew-onboard-loading crew-onboard-loading--error">
          <img
            src={logoSrc}
            alt="Leiper's Creek Timber Frames"
            className="crew-onboard-loading-mark"
            onError={() => {
              if (logoSrc !== CREW_BOARD_MEDIA.logoFallback) setLogoSrc(CREW_BOARD_MEDIA.logoFallback);
            }}
          />
          <div className="crew-board-form-card">
            <h2>Link unavailable</h2>
            <p className="crew-board-muted">{error || 'This onboarding link is no longer active.'}</p>
            <Link className="crew-board-back" to="/crew-jobs">Back to raise jobs</Link>
          </div>
        </div>
      </div>
    );
  }

  const needsForms = !onboard.hasW9 || !onboard.hasPhotoId;
  const agreement = onboard.agreement;
  const signed = agreement?.status === 'signed' || onboard.status === 'pooled';
  const fields = (agreement?.fields || {}) as {
    contractorFirstName?: string;
    contractorLastName?: string;
    jobTitle?: string;
  };
  const name = contractorAgreementFullName({
    contractorFirstName: fields.contractorFirstName || onboard.firstName,
    contractorLastName: fields.contractorLastName || onboard.lastName,
  });

  return (
    <div className="crew-board crew-onboard">
      <header className="crew-board-hero crew-board-hero--detail crew-onboard-hero">
        <div className="crew-board-hero-media">
          <img src={heroSrc} alt="" className="crew-board-hero-photo is-active" />
        </div>
        <div className="crew-board-hero-veil" />
        <div className="crew-board-hero-inner">
          <div className="crew-board-brand">
            <img
              src={logoSrc}
              alt="Leiper's Creek Timber Frames"
              className="crew-board-brand-mark"
              onError={() => {
                if (logoSrc !== CREW_BOARD_MEDIA.logoFallback) setLogoSrc(CREW_BOARD_MEDIA.logoFallback);
              }}
            />
          </div>
          <p className="crew-board-kicker">1099 contractor</p>
          <h1 className="crew-onboard-title">Onboarding packet</h1>
          <p className="crew-board-dates">Welcome, {onboard.firstName}</p>
        </div>
      </header>

      <main className="crew-board-main crew-board-detail crew-onboard-shell">
        <section className="crew-board-copy">
          <p>
            Leiper&apos;s Creek Timber Frames needs a W-9, a photo ID, and a signed independent
            contractor agreement before you can be confirmed onto a raise roster.
          </p>
        </section>

        {needsForms ? (
          <section className="crew-board-form-card">
            <h2>Upload your packet</h2>
            <p className="crew-board-muted">
              W-9 as a PDF. Photo ID as a PDF or image of a government-issued ID.
            </p>
            <p className="crew-onboard-w9-download">
              Need a blank form?{' '}
              <a href="/forms/fw9.pdf" download="IRS-Form-W-9.pdf" target="_blank" rel="noreferrer">
                Download Form W-9 (PDF)
              </a>
            </p>
            <form onSubmit={e => void uploadPacket(e)}>
              <label className="crew-onboard-file">
                <span className="crew-onboard-file-label">
                  <FileCheck2 size={18} aria-hidden />
                  W-9 (PDF)
                </span>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={e => setW9File(e.target.files?.[0] || null)}
                  required
                />
                <span className="crew-onboard-file-name">
                  {w9File ? w9File.name : 'No file chosen'}
                </span>
              </label>
              <label className="crew-onboard-file">
                <span className="crew-onboard-file-label">
                  <IdCard size={18} aria-hidden />
                  Photo ID
                </span>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp"
                  onChange={e => setIdFile(e.target.files?.[0] || null)}
                  required
                />
                <span className="crew-onboard-file-name">
                  {idFile ? idFile.name : 'No file chosen'}
                </span>
              </label>
              {actionError && <p className="crew-board-error">{actionError}</p>}
              <button type="submit" disabled={busy || !w9File || !idFile}>
                {busy ? 'Uploading…' : 'Submit W-9 and ID'}
              </button>
            </form>
          </section>
        ) : (
          <section className="crew-board-form-card crew-board-success">
            <h2>Packet received</h2>
            <p>
              {onboard.formsApproved
                ? 'The office approved your W-9 and photo ID.'
                : 'Thanks — the office will review your W-9 and photo ID next.'}
            </p>
          </section>
        )}

        {agreement && (
          <section className="crew-board-form-card crew-onboard-agreement">
            <h2>Independent contractor agreement</h2>
            <ContractorOnboardAgreement
              payload={agreement}
              contractorName={name}
              signed={signed}
              busy={busy}
              onSign={sig => void handleSign(sig)}
            />
            {actionError && !needsForms && <p className="crew-board-error">{actionError}</p>}
          </section>
        )}
      </main>
    </div>
  );
};

export default PublicCrewOnboardPage;
